import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "@/lib/utils";

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
  // Check if grid is being used via className
  const isGrid = className && typeof className === 'string' && className.includes('grid');
  
  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        // Default: inline-flex layout (left-aligned)
        !isGrid && "inline-flex h-10 items-center justify-start rounded-xl bg-muted p-1 text-muted-foreground min-w-0 w-full text-left",
        // Grid layout: equal-width columns with centered content in each cell
        isGrid && "grid h-10 items-center justify-items-center rounded-xl bg-muted p-1 text-muted-foreground min-w-0 w-full",
        className,
      )}
      {...props}
    />
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center rounded-xl px-3 py-1.5 text-sm font-medium ring-offset-background transition-all text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
      "min-w-0 max-w-full overflow-hidden w-full", // Prevent overflow, full width in grid cells
      "whitespace-nowrap text-ellipsis", // Single line with ellipsis for tabs
      "text-center", // Center text within the tab
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border focus-visible:ring-offset-2",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
